import { ReactNode } from "react";

import { MOLIT_TEMPLATE } from "@buildpy/core/modules/contract/clauses";
import type {
  ContractPrintModel,
  PrintClause,
  PrintField,
  PrintParty,
  PrintPaymentRow,
} from "@buildpy/core/modules/contract/print-model";
import PrintSheet, { SheetField, SheetTitle, TD, TH } from "@/modules/print/sheet";

// print-model 은 해시(node:crypto)에 닿아 브라우저로 못 온다 — 같은 값을 clauses 에서 읽는다.
const MOLIT_CONDITIONS_TITLE = MOLIT_TEMPLATE.name;

interface Props {
  model: ContractPrintModel;
}

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <p className="mb-1 mt-4 text-xs font-semibold">{children}</p>
);

const Notices = ({ notices }: { notices: string[] }) =>
  notices.length === 0 ? null : (
    <div className="mb-3 border border-[#d4a72c] bg-[#fff8e1] px-2.5 py-1.5 text-xs leading-5">
      {notices.map((n) => (
        <p key={n}>{n}</p>
      ))}
    </div>
  );

const Head = ({ model }: Props) => (
  <>
    <Notices notices={model.notices} />
    <SheetTitle>{model.doc_title}</SheetTitle>
    <p className="mb-3 text-right text-xs text-[#525252]">계약번호 {model.contract_no}</p>
  </>
);

const Fields = ({ fields, vatNote }: { fields: PrintField[]; vatNote: string | null }) => (
  <div className="border-t border-[#d9d9d9]">
    {fields.map((f) => (
      <SheetField key={f.label} label={f.label}>
        {f.value || " "}
      </SheetField>
    ))}
    {vatNote && <p className="mt-1 text-xs text-[#525252]">※ {vatNote}</p>}
  </div>
);

const PaymentTable = ({ rows, account }: { rows: PrintPaymentRow[]; account: string }) => (
  <div className="my-1.5">
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <TH>회차</TH>
          <TH>구분</TH>
          <TH>비율</TH>
          <TH>금액</TH>
          <TH>지급 조건</TH>
          <TH>지급일</TH>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.seq}>
            <TD className="text-center">{r.seq}</TD>
            <TD>{r.label}</TD>
            <TD className="text-right">{r.ratio}</TD>
            <TD className="text-right">{r.amount}</TD>
            <TD>{r.trigger}</TD>
            <TD className="text-center">{r.due}</TD>
          </tr>
        ))}
      </tbody>
    </table>
    {account && <p className="mt-1 text-xs">납부계좌 : {account}</p>}
  </div>
);

const WarrantyTable = ({ rows }: { rows: PrintField[] }) => (
  <table className="w-full border-collapse">
    <tbody>
      {rows.map((r) => (
        <tr key={r.label}>
          <TH className="w-40 text-left">{r.label}</TH>
          <TD>{r.value}</TD>
        </tr>
      ))}
    </tbody>
  </table>
);

const Clauses = ({ clauses, model }: { clauses: PrintClause[]; model: ContractPrintModel }) => (
  <div className="space-y-2.5">
    {clauses.map((c, n) => (
      <div key={`${c.heading}-${n}`} className="text-xs leading-5">
        <p className="font-semibold">
          {c.heading}({c.title})
        </p>
        {c.blocks.map((b, i) =>
          b.kind === "payments" ? (
            <PaymentTable key={i} rows={model.payments} account="" />
          ) : (
            <p key={i} className="whitespace-pre-wrap">
              {b.text}
            </p>
          )
        )}
      </div>
    ))}
  </div>
);

const SpecialTerms = ({ model }: Props) =>
  model.special_terms.length === 0 ? null : (
    <>
      <SectionTitle>특약사항</SectionTitle>
      <Clauses clauses={model.special_terms} model={model} />
    </>
  );

const Party = ({ party }: { party: PrintParty }) => (
  <div className="min-w-0 flex-1 text-xs leading-6">
    <p className="font-semibold">{party.role}</p>
    {party.rows.map((r) => (
      <p key={r.label}>
        <span className="inline-block w-24 text-[#525252]">{r.label}</span>
        {r.value}
      </p>
    ))}
    {party.record ? (
      <p className="mt-1 border border-[#d9d9d9] px-1.5 py-1">{party.record}</p>
    ) : (
      <p className="mt-1 text-right">(인)</p>
    )}
  </div>
);

const Closing = ({ model, attachments }: Props & { attachments?: boolean }) => (
  <div className="mt-5">
    <p className="text-xs leading-5">{model.closing}</p>
    {attachments && (
      <p className="mt-2 text-xs leading-5">
        붙임서류 : 1. 민간건설공사 도급계약 일반조건 1부 · 2. 공사계약특수조건 1부 · 3. 설계서 및 산출내역서 1부
      </p>
    )}
    <p className="my-4 text-center text-sm">{model.date_line}</p>
    <div className="flex flex-col gap-4 a4:flex-row">
      <Party party={model.client} />
      <Party party={model.contractor} />
    </div>
  </div>
);

const Footer = ({ model }: Props) => (
  <div className="mt-6 space-y-1 border-t border-[#e5e5e5] pt-2 text-[10px] leading-4 text-[#525252]">
    {model.attribution && <p>출처 : {model.attribution}</p>}
    <p>{model.record_note}</p>
    {model.doc_hash && <p className="break-all font-mono">문서 해시(SHA-256) {model.doc_hash}</p>}
  </div>
);

const Terms = ({ model }: Props) => (
  <>
    <SectionTitle>하자담보</SectionTitle>
    <WarrantyTable rows={model.warranty} />
  </>
);

// 공정위·자체 조항 — 요약 뒤에 조항, 지급표가 조항 본문에 들어갔으면 별도 절은 없다.
const ClauseContract = ({ model }: Props) => (
  <PrintSheet>
    <Head model={model} />
    <Fields fields={model.fields} vatNote={model.amount.vat_note} />
    {!model.payments_in_clauses && (
      <>
        <SectionTitle>지급회차</SectionTitle>
        <PaymentTable rows={model.payments} account={model.account} />
      </>
    )}
    <Terms model={model} />
    <SectionTitle>계약 조항</SectionTitle>
    <Clauses clauses={model.clauses} model={model} />
    {model.clause_note && <p className="mt-2 text-2xs leading-5 text-[#525252]">{model.clause_note}</p>}
    {model.consumer_notice && (
      <div className="mt-3 border border-[#d9d9d9] bg-[#fafafa] p-2 text-xs leading-5">
        <p className="font-semibold">&lt;소비자 유의사항&gt;</p>
        <p>{model.consumer_notice}</p>
      </div>
    )}
    <SpecialTerms model={model} />
    <Closing model={model} />
    <Footer model={model} />
  </PrintSheet>
);

// 국토부 — 표지(15개 항목)와 붙임 일반조건을 다른 장에.
const MolitContract = ({ model }: Props) => (
  <>
    <PrintSheet>
      <Head model={model} />
      <Fields fields={model.fields} vatNote={model.amount.vat_note} />
      <SectionTitle>지급회차 (선금·기성부분금)</SectionTitle>
      <PaymentTable rows={model.payments} account={model.account} />
      <Terms model={model} />
      <SpecialTerms model={model} />
      <Closing model={model} attachments />
      <Footer model={model} />
    </PrintSheet>
    <PrintSheet>
      <p className="text-xs">붙임</p>
      <SheetTitle>{MOLIT_CONDITIONS_TITLE}</SheetTitle>
      <Clauses clauses={model.clauses} model={model} />
      <Footer model={model} />
    </PrintSheet>
  </>
);

const ContractDocument = ({ model }: Props) => (
  <div className="flex flex-col gap-y-4">
    {model.form === "MOLIT" ? <MolitContract model={model} /> : <ClauseContract model={model} />}
  </div>
);

export default ContractDocument;
